import cn from 'clsx';
import { forwardRef, Ref } from 'react';
import { SearchBarState } from '@v2/shared/lib/types';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SearchAutoCompleteProps } from '@v2/shared/lib/useSearchAutoComplete';
import SearchSuggestionsPanel from '@v2/shared/components/SearchSuggestionsPanel';

type Props = {
  searchAutoCompleteProps: SearchAutoCompleteProps;
  /**
   * The header's own state, so that focusing the search opens the mobile search bar.
   * A search rendered outside the header — the 404 page's — has none to signal and
   * passes nothing.
   */
  searchBarState?: SearchBarState;
  /**
   * The input's id, which the label, the listbox and aria-activedescendant are all
   * derived from. Required rather than defaulted: the header alone renders two of these
   * at once, and a duplicate id silently breaks both the label and the aria wiring on
   * every one of them. Naming it is the caller's job because only the caller knows how
   * many are on the page.
   */
  id: string;
  /**
   * What the empty input invites you to type, and the accessible name that goes with it.
   *
   * Defaulted rather than required because "Search..." is right for every search of the
   * whole site, which is all of them but one: the sale page searches only what is on the
   * page, and saying so is the difference between a scoped search and a broken one.
   */
  placeholder?: string;
};

function SearchForm(
  { searchAutoCompleteProps, searchBarState, id, placeholder = 'Search...' }: Props,
  ref: Ref<HTMLInputElement>
) {
  const {
    query,
    clearQuery,
    open,
    items,
    selectedItemIndex,
    isSelected,
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    onClick,
    itemsContainerProps,
    isSpacer,
  } = searchAutoCompleteProps;

  return (
    <>
      <label htmlFor={id} className="hidden">
        {placeholder}
      </label>
      <input
        id={id}
        type="text"
        title="Search the site"
        value={query}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-controls={`${id}-listbox`}
        aria-activedescendant={
          open && selectedItemIndex >= 0 ? `${id}-option-${selectedItemIndex}` : undefined
        }
        onBlur={() => {
          onBlur();
          searchBarState?.unfocus();
        }}
        onFocus={() => {
          onFocus();
          searchBarState?.focus();
        }}
        onChange={onChange}
        onKeyDown={onKeyDown}
        className={cn(
          'peer',
          'text-base font-medium',
          'flex-grow flex items-center',

          'border-[1.5px] border-r-0',
          'border-slate-300 focus:border-slate-900',
          'focus-visible:outline-none',

          'text-slate-900',
          'placeholder-slate-400',

          'rounded-l-md rounded-r-none py-1 pl-3'
        )}
        ref={ref}
        placeholder={placeholder}
      />
      <button
        type="submit"
        className={cn(
          'px-3 py-2 border-[1.5px] border-l-0 rounded-r-md rounded-l-none',
          /*
           * The button is the right-hand end of the field, so it is white for the same
           * reason the input is — and it has to say so. Preflight sets
           * `background-color: transparent` on a button but leaves an input on the
           * browser's default white, so an unstyled button here does not match the input
           * it is attached to: it paints whatever is behind the field. That went unnoticed
           * while every caller sat on white. The sale header's second strip is `bg-primary`
           * from md up, and the button came out as a brand-coloured square on a
           * brand-coloured bar.
           */
          'bg-white',
          'text-slate-400 hover:text-slate-500 active:text-slate-600',
          'border-slate-300 peer-focus:border-slate-900'
        )}
        aria-label={query ? 'Clear search query' : 'Perform search'}
        onClick={query ? () => clearQuery() : () => onClick({ title: query })}
      >
        <FontAwesomeIcon icon={query ? faTimes : faSearch} size={query ? 'lg' : '1x'} />
      </button>
      <SearchSuggestionsPanel
        open={open}
        listboxId={`${id}-listbox`}
        itemsContainerProps={itemsContainerProps}
        items={items}
        onClick={onClick}
        isSelected={isSelected}
        isSpacer={isSpacer}
      />
    </>
  );
}

export default forwardRef(SearchForm);
